import React, { useCallback, useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import {
  FaArrowDown,
  FaArrowUp,
  FaCheckCircle,
  FaExclamationTriangle,
  FaHandHoldingUsd,
  FaMoneyBillWave,
  FaPlus,
  FaQuestionCircle,
  FaShoppingBag,
  FaTruck,
  FaUndoAlt,
  FaUniversity,
  FaWallet,
} from "react-icons/fa";
import ChartOfAccountView from "../ChartOfAccountView/ChartOfAccountView";
import "../product/AdminProducts.css";
import "./MoneyOverview.css";

const money = (value) => `Rs. ${Math.round(Number(value || 0)).toLocaleString()}`;

const KIND_ICON = {
  order: <FaShoppingBag />,
  payment: <FaHandHoldingUsd />,
  refund: <FaUndoAlt />,
  manual: <FaPlus />,
};

const WORDS = [
  ["Earned", "Product sales plus delivery fees, minus refunds. Counted when an order is placed. A cancelled order is taken out again."],
  ["Collected", "Money you actually have: cash the courier handed over and confirmed bank transfers."],
  ["Still to collect", "Cash on delivery orders that are placed but not paid yet. It becomes collected when you mark the order delivered, or, for parcels booked with a courier, when the courier pays you out."],
  ["Profit", "Product sales minus refunds and what the products cost you. Needs a cost price on your products."],
  ["Accountant view", "The full books (chart of accounts and ledger) for an accountant. You do not need it for daily work."],
];

/**
 * Money page for store owners: what the store earned, where that money is
 * now, and what happened lately, in plain words. The accountant view (chart
 * of accounts and ledger) reads the same books and is one tab away.
 */
export default function MoneyOverview() {
  const location = useLocation();
  const history = useHistory();
  const view = new URLSearchParams(location.search).get("view") === "accounts" ? "accounts" : "overview";
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(false);
  const [showWords, setShowWords] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`/api/admin/accounting/money?period=${period}`)
      .then((res) => {
        if (res.data.status === 200) setData(res.data);
      })
      .catch(() => swal("Could not load", "The money summary did not load. Please refresh the page.", "error"))
      .finally(() => setLoading(false));
  }, [period]);

  useEffect(() => {
    document.title = "Money — Admin";
  }, []);

  useEffect(() => {
    if (view === "overview") load();
  }, [load, view]);

  const setView = (next) => history.replace(next === "accounts" ? "/admin/chart_of_account?view=accounts" : "/admin/chart_of_account");

  const fixBooks = () => {
    setFixing(true);
    axios.post("/api/admin/accounting/fix-books")
      .then((res) => {
        swal(res.data.status === 200 ? "Done" : "Not fixed", res.data.message, res.data.status === 200 ? "success" : "error");
        load();
      })
      .catch(() => swal("Not fixed", "Please try again.", "error"))
      .finally(() => setFixing(false));
  };

  const tabs = (
    <div className="mo-tabs" role="tablist">
      <button type="button" role="tab" aria-selected={view === "overview"} className={view === "overview" ? "is-active" : ""} onClick={() => setView("overview")}>
        Overview
      </button>
      <button type="button" role="tab" aria-selected={view === "accounts"} className={view === "accounts" ? "is-active" : ""} onClick={() => setView("accounts")}>
        Accountant view
      </button>
    </div>
  );

  if (view === "accounts") {
    return (
      <div>
        {tabs}
        <div className="mo-accountant-note">
          <FaQuestionCircle /> Full books for an accountant. The Overview tab shows the same numbers in plain words.
        </div>
        <ChartOfAccountView />
      </div>
    );
  }

  const earned = data?.earned;
  const now = data?.money_now;
  const profit = data?.profit;
  const collected = now ? now.cash + now.bank : 0;
  const totalNow = now ? collected + now.to_collect : 0;
  const share = (value) => (totalNow > 0 ? Math.max(0, (value / totalNow) * 100) : 0);

  return (
    <div className="ap mo">
      {tabs}

      <div className="ap-head">
        <div>
          <h4>Money</h4>
          <p>What your store earned, where that money is now, and what is still to collect.</p>
        </div>
        <div className="mo-periods" role="group" aria-label="Period">
          {Object.entries(data?.period?.periods || { "30d": "Last 30 days" }).map(([key, label]) => (
            <button type="button" key={key} className={period === key ? "is-active" : ""} onClick={() => setPeriod(key)} aria-pressed={period === key}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {data && (
        data.health.ok ? (
          <div className="mo-health is-ok"><FaCheckCircle /> Your books are in order. Every order is recorded.</div>
        ) : (
          <div className="mo-health is-warn">
            <FaExclamationTriangle />
            <div className="flex-grow-1">{data.health.problems.map((problem) => <div key={problem}>{problem}</div>)}</div>
            <button type="button" className="ap-btn ap-btn-primary" onClick={fixBooks} disabled={fixing}>{fixing ? "Fixing…" : "Fix now"}</button>
          </div>
        )
      )}

      {/* Headline numbers for the chosen period */}
      <div className={`mo-cards ${loading ? "is-loading" : ""}`}>
        <div className="mo-card is-main">
          <span className="mo-card-icon"><FaMoneyBillWave /></span>
          <span className="mo-card-label">Earned · {data?.period?.label || "…"}</span>
          <strong className="mo-card-value">{earned ? money(earned.total) : "—"}</strong>
          <span className="mo-card-note">{earned ? `${earned.orders} order${earned.orders === 1 ? "" : "s"} · average ${money(earned.average_order)}` : ""}</span>
        </div>
        <div className="mo-card">
          <span className="mo-card-icon is-green"><FaWallet /></span>
          <span className="mo-card-label">Collected so far</span>
          <strong className="mo-card-value">{now ? money(collected) : "—"}</strong>
          <span className="mo-card-note">Cash and bank, all time</span>
        </div>
        <div className="mo-card">
          <span className="mo-card-icon is-amber"><FaTruck /></span>
          <span className="mo-card-label">Still to collect</span>
          <strong className="mo-card-value">{now ? money(now.to_collect) : "—"}</strong>
          <span className="mo-card-note">
            {now ? `${now.orders_to_collect} unpaid cash on delivery order${now.orders_to_collect === 1 ? "" : "s"}` : ""}
            {now?.with_courier > 0 ? ` · ${money(now.with_courier)} delivered, waiting for courier payout` : ""}
          </span>
        </div>
        <div className="mo-card">
          <span className="mo-card-icon is-violet"><FaArrowUp /></span>
          <span className="mo-card-label">Profit · {data?.period?.label || "…"}</span>
          {profit?.known ? (
            <>
              <strong className="mo-card-value">{money(profit.amount)}</strong>
              <span className="mo-card-note">After product cost of {money(profit.product_cost)}</span>
            </>
          ) : (
            <>
              <strong className="mo-card-value is-muted">Not known</strong>
              <span className="mo-card-note">Add a cost price to your products to see profit.</span>
            </>
          )}
        </div>
      </div>

      <div className="mo-grid">
        {/* How the earned amount is made up */}
        <section className="ap-card mo-panel">
          <h5>How you earned it</h5>
          <p className="mo-panel-sub">{data?.period?.label || ""}</p>
          {earned && (
            <ul className="mo-sum">
              <li><span><FaShoppingBag className="mo-sum-icon" /> Product sales</span><strong>{money(earned.product_sales)}</strong></li>
              <li><span><FaTruck className="mo-sum-icon" /> Delivery fees charged</span><strong>+ {money(earned.delivery)}</strong></li>
              <li className={earned.refunds > 0 ? "is-minus" : ""}><span><FaUndoAlt className="mo-sum-icon" /> Refunds to customers</span><strong>− {money(earned.refunds)}</strong></li>
              <li className="is-total"><span>Earned</span><strong>{money(earned.total)}</strong></li>
            </ul>
          )}
          {earned?.discounts_given > 0 && (
            <p className="mo-foot-note">You gave {money(earned.discounts_given)} in discounts and bundle savings. It is already taken off product sales above.</p>
          )}
        </section>

        {/* Where the money sits right now */}
        <section className="ap-card mo-panel">
          <h5>Where your money is now</h5>
          <p className="mo-panel-sub">All time, as of today</p>
          {now && (
            <>
              <div className="mo-bar" aria-hidden="true">
                <span className="is-cash" style={{ width: `${share(now.cash)}%` }} />
                <span className="is-bank" style={{ width: `${share(now.bank)}%` }} />
                <span className="is-due" style={{ width: `${share(now.to_collect)}%` }} />
              </div>
              <ul className="mo-where">
                <li>
                  <span className="mo-dot is-cash" />
                  <div><strong>Cash collected</strong><small>Paid on delivery and handed to you</small></div>
                  <b>{money(now.cash)}</b>
                </li>
                <li>
                  <span className="mo-dot is-bank" />
                  <div><strong><FaUniversity className="me-1" />In the bank</strong><small>Bank transfers you confirmed</small></div>
                  <b>{money(now.bank)}</b>
                </li>
                <li>
                  <span className="mo-dot is-due" />
                  <div><strong>Still to collect</strong><small>With customers or couriers until delivery</small></div>
                  <b>{money(now.to_collect)}</b>
                </li>
                {now.with_courier > 0 && (
                  <li className="mo-where-sub">
                    <span className="mo-dot" />
                    <div><strong>of which: delivered, courier has not paid yet</strong><small>Moves to the bank when the courier pays out</small></div>
                    <b>{money(now.with_courier)}</b>
                  </li>
                )}
              </ul>
              <Link to="/admin/orders" className="mo-link">See unpaid orders →</Link>
            </>
          )}
        </section>
      </div>

      {/* Latest money events */}
      <section className="ap-card mo-panel">
        <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
          <div>
            <h5>Recent money activity</h5>
            <p className="mo-panel-sub">Latest 15 events</p>
          </div>
          <button type="button" className="mo-text-btn" onClick={() => setView("accounts")}>Open the full books →</button>
        </div>
        {data && !data.activity.length && <p className="text-muted small mb-0">Nothing yet. Orders and payments will show here.</p>}
        <ul className="mo-activity">
          {(data?.activity || []).map((item) => (
            <li key={item.id} className={item.cancelled ? "is-cancelled" : ""}>
              <span className={`mo-activity-icon is-${item.kind}`}>{KIND_ICON[item.kind] || <FaPlus />}</span>
              <div className="mo-activity-text">
                {item.order_id ? <Link to={`/admin/orders/${item.order_id}`}>{item.title}</Link> : <strong>{item.title}</strong>}
                <small>{item.date} · {item.note}</small>
              </div>
              <b className={item.kind === "refund" ? "is-minus" : ""}>
                {item.kind === "refund" ? <FaArrowDown size={10} /> : null} {money(item.amount)}
              </b>
            </li>
          ))}
        </ul>
      </section>

      {/* Plain meanings of the words used */}
      <section className="ap-card mo-panel">
        <button type="button" className="mo-words-toggle" onClick={() => setShowWords((open) => !open)} aria-expanded={showWords}>
          <FaQuestionCircle /> What do these words mean?
        </button>
        {showWords && (
          <dl className="mo-words">
            {WORDS.map(([term, meaning]) => (
              <div key={term}><dt>{term}</dt><dd>{meaning}</dd></div>
            ))}
          </dl>
        )}
      </section>
    </div>
  );
}
